import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import {
  Button,
  Dropdown,
  FormItem,
  Modal,
  tailwindOverride,
  Typography,
} from '@getsynapse/design-system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  fetchProjectTemplatesForSelection,
  selectAllProjectTemplates,
  selectProjectTemplatesStatus,
} from 'state/ProjectTemplates/projectTemplatesSlice';
import { SLICE_STATUS } from 'utils/constants';
import AddProjectIcon from 'assets/icons/template.svg';
import { ProjectTemplate } from 'types/store/normalized';
import useImportProjects from 'Pages/ProjectsListPage/hooks/useImportProjects';
import { useImport } from 'Hooks/useImport';

interface ImportProjectModalProps {
  showModal: boolean;
  onClose: () => void;
}

const ImportProjectModal: React.FC<ImportProjectModalProps> = ({
  showModal,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { teamScopingProjectTemplates = false } = useFlags();
  const projectTemplates: ProjectTemplate[] = useSelector(
    selectAllProjectTemplates
  );
  const templatesStatus = useSelector(selectProjectTemplatesStatus);
  const isLoadingTemplates = templatesStatus === SLICE_STATUS.LOADING;
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate>();
  const [isLoading, setIsLoading] = useState(false);
  const onCloseModal = () => {
    setSelectedTemplate(undefined);
    onClose();
  };
  const { currentUser, settings, onData } = useImportProjects(selectedTemplate);
  const { startImport } = useImport(onData, currentUser, settings);

  const onStartImport = () => {
    setIsLoading(true);
    startImport();
    setTimeout(() => {
      setIsLoading(false);
      onCloseModal();
    }, 1500);
  };

  useEffect(() => {
    if (showModal) {
      dispatch(
        fetchProjectTemplatesForSelection({
          useNewApi: teamScopingProjectTemplates,
        })
      );
    }
  }, [dispatch, showModal, teamScopingProjectTemplates]);

  const selectTemplate: (templateId: string) => void = useCallback(
    (templateId) => {
      const selectedTemplate = projectTemplates.find(
        (template) => template.id === templateId
      );

      if (selectedTemplate) {
        setSelectedTemplate(selectedTemplate);
      }
    },
    [projectTemplates]
  );

  return (
    <Modal
      title={intl.get('IMPORT_PROJECT_MODAL.TITLE')}
      titleIcon={{ src: AddProjectIcon }}
      isOpen={showModal}
      size='medium'
      closeModal={onCloseModal}
      aria-label={intl.get('IMPORT_PROJECT_MODAL.TITLE')}
      data-testid='import-project-select-template-modal'
    >
      <Typography variant='body' className='mb-4 text-primary'>
        {intl.get('IMPORT_PROJECT_MODAL.DESCRIPTION')}
      </Typography>
      <FormItem
        label={intl.get(
          'PROJECTS_LIST_PAGE.CREATE_PROJECT_MODAL.TEMPLATE_FIELD_LABEL'
        )}
        className='w-full'
      >
        <Dropdown
          disabled={isLoadingTemplates}
          options={projectTemplates}
          getOptionLabel={(option: ProjectTemplate) => option.name}
          onChange={(option: ProjectTemplate) => selectTemplate(option.id!)}
          triggerProps={{ 'data-testid': 'select-template' }}
          placeholder={intl.get(
            'IMPORT_PROJECT_MODAL.TEMPLATE_FIELD_PLACEHOLDER'
          )}
          renderOption={(
            option: ProjectTemplate,
            isSelected: boolean,
            selectOption,
            { className, ...otherProps }
          ) => (
            <li
              {...otherProps}
              data-value={option.id}
              data-testid={option.id}
              key={option.id}
              className={tailwindOverride('group', className, {
                'hover:bg-primary focus-visible:bg-primary': isSelected,
              })}
              onClick={selectOption}
            >
              <div className='flex w-100 flex-col space-y-1 '>
                <Typography
                  variant='label'
                  weight='medium'
                  className='text-primary'
                >
                  {option.name}
                </Typography>
                {option.description && (
                  <Typography variant='caption' className='text-neutral'>
                    {option.description}
                  </Typography>
                )}
              </div>
            </li>
          )}
        />
      </FormItem>

      <div className='flex mt-6 space-x-2'>
        <Button
          key={selectedTemplate?.id}
          variant='primary'
          onClick={onStartImport}
          disabled={!selectedTemplate}
          loading={isLoading}
          className='h-10'
        >
          {intl.get('IMPORT')}
        </Button>
        <Button variant='tertiary' className='h-10' onClick={onCloseModal}>
          {intl.get('CANCEL')}
        </Button>
      </div>
    </Modal>
  );
};

export default ImportProjectModal;
